import type { IEvaultApi } from './use-evault-api';
import type { IVaultRegistry } from './types';

import { useCallback, useRef, useState } from 'react';

import { useNotification } from 'hooks';

import { EVAULT_API } from './use-evault-api';

/** `GET /vault-registry/:id?live=1` — the stored registry plus a fresh check
 *  against the ledger. `live` is `null` when `?live=1` was not sent, an error
 *  object when the ledger could not answer (still HTTP 200), or the check
 *  itself on success. */
interface IVaultRegistryLive {
	documentId: string;
	registry: IVaultRegistry | null;
	live:
		| { matches: boolean; status: string; owner: string | null; fullySigned: boolean; at: string }
		| { error: string }
		| null;
}

/** One document's live verify result, kept by id so the drawer can render it
 *  without refetching the list row it opened with. */
export interface IRegistryVerifyResult {
	at?: string;
	matches?: boolean;
	status?: string;
	owner?: string | null;
	error?: string;
}

/**
 * "Verify on chain" — a live, on-demand re-check of one document's ledger entry.
 *
 * `?live=1` is the expensive path: the server re-reads the ledger and writes
 * `registry.lastVerify`, so — like the Integrity section's own `ensureEvidence`
 * — it fires only on a click, never on drawer open.
 *
 * Keyed by document id, the same shape as `use-view-copy`'s `busyId`/result
 * pair, so verifying one document in one drawer never disables a button for
 * any other row.
 */
export const useRegistryVerify = (api: IEvaultApi) => {
	const { errorNotification } = useNotification();
	const [busyId, setBusyId] = useState<string | null>(null);
	/* The lock the guard actually reads. `busyId` is state, so a second click
	   landing before React commits the disabling render would read the stale
	   closure and fire a second request; a ref is current on the same tick. */
	const inFlight = useRef<string | null>(null);
	const [result, setResult] = useState<Record<string, IRegistryVerifyResult>>({});

	const verify = useCallback(
		async (docId: string): Promise<void> => {
			if (!docId || inFlight.current) return;
			inFlight.current = docId;
			setBusyId(docId);

			const outcome = await api.get<IVaultRegistryLive>(
				`${EVAULT_API.VAULT_REGISTRY}/${docId}?live=1`
			);

			inFlight.current = null;
			setBusyId(null);

			if (outcome.kind !== 'ok') {
				errorNotification(outcome.message);
				return;
			}

			const { live } = outcome.data;
			if (!live) return;

			setResult(prev => ({
				...prev,
				[docId]: 'error' in live ? { error: live.error } : { ...live },
			}));
		},
		[api, errorNotification]
	);

	return { verify, busyId, result };
};
